import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { WAITING_SUB_STATUSES } from 'utils/constants';
import { useAppSelector } from 'redux/store';
import { selectHour12Option } from 'redux/selectors/userSettings';

interface FlowCardChildNode {
  flow_node_id: string;
  name: string;
  type: string;
  status?: string;
  sub_status?: string | null;
}

interface FlowCardData {
  label: string;
  mode: 'editor' | 'viewer';
  childNodes: FlowCardChildNode[];
  flowInstanceStatus?: string | null;
  scheduledTime?: string | null;
  hasInstance?: boolean;
}

const getStatusClass = (status?: string, sub_status?: string | null): string => {
  if (!status) return '';
  const effective = WAITING_SUB_STATUSES.includes(sub_status || '') ? 'waiting' : status;
  return `status-${effective}`;
};

const FlowCardNode = memo(({ data }: NodeProps<FlowCardData>) => {
  const { label, mode, childNodes = [], flowInstanceStatus, scheduledTime, hasInstance } = data;
  const isViewer = mode === 'viewer';
  const hour12 = useAppSelector(selectHour12Option);

  // Format time for display
  const formattedTime = scheduledTime
    ? new Date(scheduledTime).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12,
      })
    : null;

  return (
    <div className={`monitoring-flow-card ${isViewer && flowInstanceStatus ? getStatusClass(flowInstanceStatus) : ''}`}>
      <Handle type="target" position={Position.Left} style={{ background: '#888' }} />

      <div className="flow-card-header">
        <strong className="flow-card-title">{label}</strong>
        <div className="flow-card-header-right">
          {isViewer && flowInstanceStatus && <span className={`flow-card-status-badge ${getStatusClass(flowInstanceStatus)}`}>{flowInstanceStatus}</span>}
          {isViewer && formattedTime && <span className="flow-card-schedule">{formattedTime}</span>}
        </div>
      </div>

      <div className="flow-card-body">
        {isViewer && !hasInstance ? (
          <div className="flow-card-empty">No instance in date range</div>
        ) : childNodes.length > 0 ? (
          childNodes.map((child) => (
            <div key={child.flow_node_id} className={`flow-card-child-node ${isViewer ? getStatusClass(child.status, child.sub_status) : ''}`}>
              <span className="child-node-name">{child.name}</span>
            </div>
          ))
        ) : (
          <div className="flow-card-empty">{isViewer ? 'No child nodes found' : 'No child nodes'}</div>
        )}
      </div>

      <Handle type="source" position={Position.Right} style={{ background: '#888' }} />
    </div>
  );
});

FlowCardNode.displayName = 'FlowCardNode';
export default FlowCardNode;
